import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype, QuantityRuntype } from '../backendTypes';
import { getFileName, getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { QuoteRequestOfferDTORuntype, QuoteRequestOfferImportDTORuntype } from '../offer';
import { PartDTORuntype } from '../part';
import { UserDTORuntype } from '../user';
import {
    EmailHistoryEventDTORuntype,
    QuoteRequestDTORuntype,
    QuoteRequestFieldNameRuntype,
    QuoteRequestLineItemApprovalStatus,
    QuoteRequestLineItemDTORuntype,
    SendQuoteRequestsResponseRuntype,
    SupplierSelectionStrategyRuntype,
} from './quoteRequestBackendTypes';

export const quoteRequestEndpoints = {
    'GET /quote-request/:id': endpoint({
        description: 'Get a quote request',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: QuoteRequestDTORuntype,
    }),

    'POST /quote-request/find': endpoint({
        description: 'Finds quote requests',
        requestBody: z.object({
            // The backend errors unless quote_request_ids or negotiation_v2_id is given.
            rfq_id: z.string().optional(),
            quote_request_ids: z.array(z.string()).optional(),
            negotiation_v2_id: z.string().optional(),
            quote_request_numbers: z.array(z.number()).optional(),
        }),
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.object({
            items: z.array(QuoteRequestDTORuntype),
        }),
    }),

    'PATCH /quote-request/:id': endpoint({
        description: 'Updates a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        responseBody: z.string(),
        queryParams: z.undefined(),
        requestBody: z.object({
            due_date: z.string().nullable().optional(),
            discarded: z.boolean().optional(),
            show_customer: z.boolean().optional(),
            show_target_price: z.boolean().optional(),
            notes: z.string().nullable().optional(),
            hidden_fields: z.array(QuoteRequestFieldNameRuntype).optional(),
            required_fields: z.array(QuoteRequestFieldNameRuntype).optional(),
            include_project_name: z.boolean().optional(),
            include_customer_name: z.boolean().optional(),
            include_pcb_specification: z.boolean().optional(),
            include_panel_specification: z.boolean().optional(),
            include_mail_attachments: z.boolean().optional(),
            include_custom_part_attachments: z.boolean().optional(),
            include_excel_quote_request: z.boolean().optional(),
            include_exchange_rates: z.boolean().optional(),
        }),
        invalidates: ['POST /quote-request/find', 'GET /quote-request/:id', 'GET /quote-request/:id/line-items'],
    }),

    'DELETE /quote-request/:id': endpoint({
        description: 'Deletes a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
    }),

    'DELETE /quote-request/delete-many': endpoint({
        description: 'Deletes mulitple quote requests',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            quote_request_ids: z.array(z.string()),
        }),
        responseBody: z.string(),
    }),

    'POST /quote-request/draft': endpoint({
        description: 'Creates a quote request draft',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            supplier_ids: z.array(z.string()),
            items: z.array(
                z.object({
                    // negotiation part options
                    part_option_id: z.number(),
                    part: PartDTORuntype,
                }),
            ),
            supplier_selection_strategy: SupplierSelectionStrategyRuntype,
            template_id: z.string().optional().nullable(),
        }),
        responseBody: z.object({
            quote_requests: z.array(
                z.object({
                    id: z.string(),
                    quote_request_line_items: z.number(),
                }),
            ),
        }),
        invalidates: [
            'GET /quote-request/:id/line-items',
            'POST /quote-request/find',
            'GET /quote-request/:id',
            'POST /negotiation/line-items/part-options-with-quote-request-line-items',
        ],
    }),

    'POST /quote-request/rfq/:rfqId/draft': endpoint({
        description: 'Creates a draft quote request from an RFQ',
        pathParams: z.object({
            rfqId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.object({
            template_id: z.string().optional().nullable(),
            supplier_ids: z.array(z.string()),
            supplier_selection_strategy: SupplierSelectionStrategyRuntype,
            items: z.array(
                z.object({
                    solution_configuration_id: z.string(),
                    resolved_part: PartDTORuntype,
                    part_option: PartDTORuntype,
                    delivery_batch_size: z.number().optional(),
                }),
            ),
        }),
        responseBody: z.object({
            quote_requests: z.array(
                z.object({
                    id: z.string(),
                    quote_request_line_items: z.number(),
                }),
            ),
        }),
        invalidates: [
            'GET /quote-request/:id/line-items',
            'POST /quote-request/find',
            'GET /quote-request/:id',
            'POST /negotiation/line-items/part-options-with-quote-request-line-items',
        ],
    }),

    'POST /quote-request/negotiation-v2/:id/draft': endpoint({
        description: 'Creates a draft quote request from a negotiation v2',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.object({
            template_id: z.string().optional().nullable(),
            supplier_ids: z.array(z.string()),
            supplier_selection_strategy: SupplierSelectionStrategyRuntype,
            items: z.array(
                z.object({
                    solution_configuration_id: z.string(),
                    resolved_part: PartDTORuntype,
                    part_option: PartDTORuntype,
                    delivery_batch_size: z.number().optional(),
                }),
            ),
        }),
        responseBody: z.object({
            quote_requests: z.array(
                z.object({
                    id: z.string(),
                    quote_request_line_items: z.number(),
                }),
            ),
        }),
        invalidates: [
            'GET /quote-request/:id/line-items',
            'POST /quote-request/find',
            'GET /quote-request/:id',
            'POST /negotiation/line-items/part-options-with-quote-request-line-items',
        ],
    }),

    'GET /quote-request/:id/line-items': endpoint({
        description: 'Finds quote request line items for a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(QuoteRequestLineItemDTORuntype),
        }),
    }),

    'POST /quote-request/line-items': endpoint({
        description: 'Finds quote request line items by quote request ids',
        requestBody: z.object({
            quote_request_ids: z.array(z.string()),
        }),
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.object({
            items: z.array(QuoteRequestLineItemDTORuntype),
        }),
    }),

    'GET /quote-request/has-unapproved-pcb': endpoint({
        description:
            'Returns whether any PCB referenced by the given quote requests is missing one of the four PCB workflow approvals',
        requestBody: z.undefined(),
        pathParams: z.undefined(),
        queryParams: z.object({
            quote_request_ids: z.string(),
        }),
        responseBody: z.object({
            has_unapproved_pcb: z.boolean(),
        }),
    }),

    'POST /quote-request/line-item/update-many': endpoint({
        description: 'Updates multiple quote request line items',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            updates: z.array(
                z.object({
                    id: z.number(),
                    target_price: MonetaryValueBackendRuntype.optional().nullable(),
                    delivery_lot_size: QuantityRuntype.optional().nullable(),
                    approval_status: z.enum(QuoteRequestLineItemApprovalStatus).optional(),
                    approval_status_notes: z.string().nullable().optional(),
                }),
            ),
        }),
        responseBody: z.string(),
        invalidates: [
            'POST /quote-request/line-items',
            'GET /quote-request/:id/line-items',
            'POST /quote-request/find',
        ],
    }),

    'POST /quote-request/line-item/delete': endpoint({
        description: 'Deletes a quote request line item',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.number()),
        }),
        responseBody: z.string(),
        invalidates: [
            'POST /negotiation/line-items/part-options-with-quote-request-line-items',
            'POST /quote-request/line-items',
            'GET /quote-request/:id/line-items',
        ],
    }),

    'POST /quote-request/line-items/duplicate': endpoint({
        description: 'Duplicates quote request line items',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            items: z.array(
                z.object({
                    quote_request_id: z.string(),
                    quote_request_line_items: z.array(z.number()),
                }),
            ),
        }),
        responseBody: z.string(),
        invalidates: [
            'POST /quote-request/line-items',
            'GET /quote-request/:id/line-items',
            'POST /quote-request/find',
        ],
    }),

    'GET /quote-request/:id/offers': endpoint({
        description: 'Gets offers for a quote request',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(QuoteRequestOfferDTORuntype),
    }),

    'POST /quote-request/:id/add-offers': endpoint({
        description: 'Adds offers to a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.object({
            clear_old_offers: z.boolean(),
        }),
        requestBody: z.object({
            inputs: z.array(QuoteRequestOfferImportDTORuntype),
        }),
        responseBody: z.array(
            z.object({
                index: z.number(),
                result: z.unknown(),
            }),
        ),
        invalidates: ['POST /quote-request/find', 'GET /quote-request/:id/line-items'],
    }),

    'DELETE /quote-request/:id/offers': endpoint({
        description: 'Deletes offers offers for a quote request',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ deleted: z.number() }),
    }),

    'DELETE /quote-request/line-item/delete-offer': endpoint({
        description: 'Delete offers for a set of quote request line items',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.number()),
        }),
        responseBody: z.object({ deleted: z.number() }),
        invalidates: ['POST /quote-request/line-items', 'GET /quote-request/:id/line-items'],
    }),

    'POST /quote-request/send-reminders': endpoint({
        description: 'Sends a reminder for a quote request',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ ids: z.array(z.string()) }),
        responseBody: SendQuoteRequestsResponseRuntype,
    }),

    'POST /quote-request/send-many': endpoint({
        description: 'Sends multiple quote requests',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            include_pcb_specification: z.boolean(),
            include_shipping_panel_specification: z.boolean(),
            include_mail_attachments: z.boolean(),
            mail_data: z.array(
                z.object({
                    quote_request_id: z.string(),
                    recipient_emails: z.array(z.object({ name: z.string().optional(), email: z.string() })),
                    cc_emails: z.array(z.object({ name: z.string().nullable(), email: z.string() })),
                    email: z.object({ subject: z.string(), body: z.string() }),
                }),
            ),
        }),
        responseBody: SendQuoteRequestsResponseRuntype,
        invalidates: ['POST /quote-request/find', 'GET /quote-request/:id', 'GET /quote-request/:id/line-items'],
    }),

    'GET /quote-request/:id/email-logs': endpoint({
        description: 'Gets the email history for a quote request',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(EmailHistoryEventDTORuntype),
    }),

    'GET /quote-request/:id/additional-files': endpoint({
        description: 'Gets additional files for a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(z.object({ name: z.string(), url: z.string() })),
    }),

    'GET /quote-request/:id/additional-files/upload-link': endpoint({
        description: 'Gets an upload link for additional files for a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: ['GET /quote-request/:id/additional-files'],
    }),

    'DELETE /quote-request/:id/additional-files': endpoint({
        description: 'Deletes additional files for a quote request',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            file_name: z.string(),
        }),
        responseBody: z.string(),
        invalidates: ['GET /quote-request/:id/additional-files'],
    }),

    'GET /quote-request/:id/download-excel': endpoint({
        description: 'Downloads a quote request as an Excel file',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'POST /quote-request/download-attachments': endpoint({
        description: 'Downloads attachments for a quote request',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            id: z.object({
                type: z.union([z.literal('QuoteRequestId'), z.literal('QuoteRequestLineItemId')]),
                data: z.string().or(z.number()),
            }),
        }),
        handleResponse: async (response) => {
            await assertSuccessfulResponseGenericErrorCode(response, 'POST /quote-request/download-attachments');
            return {
                blob: await response.blob(),
                filename: getFileName(response.headers.get('content-disposition') ?? 'attachments.zip'),
            };
        },
        responseBody: z.object({
            blob: z.instanceof(Blob),
            filename: z.string(),
        }),
    }),

    'GET /quote-request/:id/commentators': endpoint({
        description: 'Gets commentators for a quote request',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        responseBody: z.object({ data: z.array(UserDTORuntype) }),
        requestBody: z.undefined(),
    }),
} satisfies Record<string, Endpoint>;

type Endpoint = ReturnType<typeof endpoint>;
